import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Heart, User, Search, Menu, X, LogOut, ArrowRight, Package, Settings, Wrench } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAdminMode } from '../contexts/AdminModeContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { useLanguage } from '../contexts/LanguageContext';
import { appToast } from '../utils/toast';
import { buildAuthRedirectState } from '../utils/authRedirect';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar: React.FC = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const { adminMode, toggleAdminMode } = useAdminMode();
  const { cart } = useCart();
  const { ids: wishlistIds } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  // Favourites require an account. For guests, prompt sign-up instead of opening
  // the (protected) favourites page.
  const handleFavouritesClick = (e: React.MouseEvent) => {
    setIsMenuOpen(false);
    if (!user) {
      e.preventDefault();
      appToast.error(t.toasts.wishlistLoginRequired);
      navigate('/register', { state: buildAuthRedirectState(location) });
    }
  };

  const cartItemCount = cart?.items ? cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0) : 0;

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className={`relative sticky top-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-neutral-200' 
        : 'bg-white shadow-sm'
    }`}>
      <div className="snow-icicles" aria-hidden="true" />
      <div className="container-custom">
        <div className="flex items-center justify-between min-h-[4.25rem] py-1.5">
          {/* Logo */}
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
            className="flex items-center group flex-shrink-0"
          >
            <img
              src="/trendhome-holiday-logo.png"
              alt={t.brandName}
              width={100}
              height={48}
              className="h-12 w-auto max-w-none object-contain object-left"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8 ml-12">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-all duration-200 relative py-2 ${
                isActive('/') 
                  ? 'text-primary-800' 
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              {t.navbar.home}
              {isActive('/') && (
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-800 rounded-full"></div>
              )}
            </Link>
            <Link 
              to="/products" 
              className={`text-sm font-medium transition-all duration-200 relative py-2 ${
                isActive('/products') 
                  ? 'text-primary-800' 
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              {t.navbar.products}
              {isActive('/products') && (
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-800 rounded-full"></div>
              )}
            </Link>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-lg mx-8">
            <div className="relative w-full group">
              <input
                type="text"
                placeholder={t.navbar.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-neutral-50 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-sm group-hover:bg-white group-hover:shadow-sm group-hover:border-neutral-400"
              />
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400 group-focus-within:text-primary-500 transition-colors" />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-neutral-400 hover:text-primary-600 transition-colors"
                aria-label="Search"
              >
                <Search className="h-4 w-4" />
              </button>
            </div>
          </form>

          {/* Right side items */}
          <div className="hidden md:flex items-center space-x-4 ml-6">
            {/* Admin Mode toggle - only for admins. When on, inline edit buttons
                appear on product cards and product pages. */}
            {user?.is_admin && (
              <button
                onClick={toggleAdminMode}
                title={adminMode ? 'Admin mode: on' : 'Admin mode: off'}
                aria-pressed={adminMode}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  adminMode
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                <Wrench className="h-4 w-4" />
                <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${adminMode ? 'bg-white/40' : 'bg-neutral-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${adminMode ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </span>
              </button>
            )}

            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* Favourites */}
            <div className="relative group">
              <Link
                to={user ? '/favourites' : '/register'}
                onClick={handleFavouritesClick}
                className="relative text-neutral-600 hover:text-red-500 transition-all duration-200 flex items-center justify-center"
                title={t.navbar.favourites}
              >
                <div className="p-3 rounded-lg group-hover:bg-neutral-100 transition-colors duration-200 flex items-center justify-center min-w-[44px] min-h-[44px]">
                  <Heart className={`h-5 w-5 ${wishlistIds.length > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                  {wishlistIds.length > 0 && (
                    <span className="absolute -top-1 -right-1 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center bg-red-500 shadow-sm">
                      {wishlistIds.length > 99 ? '99+' : wishlistIds.length}
                    </span>
                  )}
                </div>
              </Link>
            </div>

            {/* Cart */}
            <div className="relative group">
              <Link 
                to="/cart" 
                className="relative text-neutral-600 hover:text-neutral-900 transition-all duration-200 flex items-center justify-center"
                title={t.navbar.shoppingCart}
              >
                <div className="p-3 rounded-lg group-hover:bg-neutral-100 transition-colors duration-200 flex items-center justify-center min-w-[44px] min-h-[44px]">
                  <ShoppingCart className="h-5 w-5" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-1 -right-1 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center bg-primary-600 shadow-sm animate-pulse">
                      {cartItemCount > 99 ? '99+' : cartItemCount}
                    </span>
                  )}
                </div>
              </Link>
            </div>

            {/* User Menu */}
            {user ? (
              <div className="relative group">
                <button className="flex items-center space-x-3 text-neutral-600 hover:text-neutral-900 transition-all duration-200 p-2.5 rounded-lg group-hover:bg-neutral-100">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center group-hover:from-primary-200 group-hover:to-primary-300 transition-all duration-200">
                    <User className="h-4 w-4 text-primary-700 group-hover:text-primary-800 transition-colors duration-200" />
                  </div>
                  <span className="text-sm font-semibold">{user.username || user.first_name || user.email}</span>
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-neutral-200">
                  <div className="px-3 py-2 border-b border-neutral-100">
                    <p className="text-xs text-neutral-500">{t.navbar.signedInAs}</p>
                    <p className="text-sm font-semibold text-neutral-900">{user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}</p>
                  </div>
                  {user.is_admin && (
                    <Link
                      to="/admin"
                      className="flex items-center space-x-3 w-full px-4 py-3 text-sm font-medium text-primary-700 hover:bg-primary-50 transition-colors"
                    >
                      <Settings className="h-4 w-4" />
                      <span>{t.navbar.adminPanel}</span>
                    </Link>
                  )}
                  <Link
                    to="/orders"
                    className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                  >
                    <Package className="h-4 w-4" />
                    <span>{t.navbar.myOrders}</span>
                  </Link>
                  <Link
                    to="/profile"
                    className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                  >
                    <User className="h-4 w-4" />
                    <span>{t.navbar.profile}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>{t.navbar.signOut}</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link 
                  to="/login" 
                  className="text-sm font-medium text-neutral-700 border border-neutral-300 hover:border-neutral-400 hover:text-neutral-900 transition-colors duration-200 px-4 py-2.5 rounded-lg hover:bg-neutral-50 flex items-center"
                >
                  {t.navbar.signIn}
                </Link>
                <Link 
                  to="/register" 
                  className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 flex items-center group"
                >
                  {t.navbar.getStarted}
                  <ArrowRight className="ml-2 h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-neutral-600 hover:text-neutral-900 transition-colors duration-200 p-2 rounded-lg hover:bg-neutral-100 flex items-center justify-center min-w-[44px] min-h-[44px]"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu with enhanced animation */}
        <div className={`md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen
            ? 'max-h-[calc(100vh-4.25rem)] overflow-y-auto opacity-100'
            : 'max-h-0 overflow-hidden opacity-0'
        }`}>
          <div className="py-4 border-t border-neutral-200 space-y-4">
            {/* Mobile Actions — language + cart/favourites at the top */}
            <div className="flex items-center justify-between">
              <LanguageSwitcher />
              <div className="flex items-center space-x-2">
                <Link
                  to={user ? '/favourites' : '/register'}
                  onClick={handleFavouritesClick}
                  aria-label={t.navbar.favourites}
                  title={t.navbar.favourites}
                  className="flex items-center space-x-2 text-neutral-600 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-neutral-50"
                >
                  <div className="relative">
                    <Heart className={`h-5 w-5 ${wishlistIds.length > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                    {wishlistIds.length > 0 && (
                      <span className="absolute -top-1 -right-1 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center bg-red-500">
                        {wishlistIds.length}
                      </span>
                    )}
                  </div>
                </Link>
                <Link
                  to="/cart"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center space-x-2 text-neutral-600 hover:text-neutral-900 transition-colors p-2 rounded-lg hover:bg-neutral-50"
                >
                  <div className="relative">
                    <ShoppingCart className="h-5 w-5" />
                    {cartItemCount > 0 && (
                      <span className="absolute -top-1 -right-1 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center bg-primary-600">
                        {cartItemCount > 99 ? '99+' : cartItemCount}
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-medium">{t.navbar.cart}</span>
                </Link>
              </div>
            </div>

            {/* Mobile Search */}
            <form onSubmit={handleSearch} className="w-full">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t.navbar.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-neutral-50 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 text-sm"
                />
                <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-neutral-400 hover:text-primary-600 transition-colors"
                  aria-label="Search"
                >
                  <Search className="h-4 w-4" />
                </button>
              </div>
            </form>

            {/* Mobile Navigation */}
            <div className="space-y-1">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  isActive('/') 
                    ? 'bg-primary-50 text-primary-800' 
                    : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                }`}
              >
                {t.navbar.home}
              </Link>
              <Link
                to="/products"
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  isActive('/products') 
                    ? 'bg-primary-50 text-primary-800' 
                    : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                }`}
              >
                {t.navbar.products}
              </Link>
            </div>

            {/* Mobile User Menu */}
            {user ? (
              <div className="pt-4 border-t border-neutral-200 space-y-1">
                {user.is_admin && (
                  <button
                    onClick={toggleAdminMode}
                    aria-pressed={adminMode}
                    className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-neutral-700 hover:bg-neutral-50 rounded-lg transition-colors"
                  >
                    <span className="flex items-center space-x-3">
                      <Wrench className="h-4 w-4" />
                      <span>Admin mode</span>
                    </span>
                    <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${adminMode ? 'bg-primary-600' : 'bg-neutral-300'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${adminMode ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </span>
                  </button>
                )}
                {user.is_admin && (
                  <Link
                    to="/admin"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center space-x-3 w-full px-4 py-3 text-sm font-medium text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    <span>{t.navbar.adminPanel}</span>
                  </Link>
                )}
                <Link
                  to="/orders"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 rounded-lg transition-colors"
                >
                  <Package className="h-4 w-4" />
                  <span>{t.navbar.myOrders}</span>
                </Link>
                <Link
                  to="/profile"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 rounded-lg transition-colors"
                >
                  <User className="h-4 w-4" />
                  <span>{t.navbar.profile}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 rounded-lg transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t.navbar.signOut}</span>
                </button>
              </div>
            ) : (
              <div className="pt-4 border-t border-neutral-200 space-y-3">
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="block w-full text-center px-4 py-3 text-sm font-medium text-neutral-700 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors duration-200"
                >
                  {t.navbar.signIn}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMenuOpen(false)}
                  className="block w-full text-center px-4 py-3 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                >
                  {t.navbar.getStarted}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 